import { createContext, useContext, useState, useCallback, PropsWithChildren, Dispatch, SetStateAction, useEffect } from 'react';
import globalVariableService, { GlobalVariableMappingGroup } from '../services/globalVariableService';

// Tipos
export interface DynamicTextMapping {
    type: 'act_type' | 'previous_answer' | 'conditional' | 'conditional_previous_answer' | 'conditional_multiple' | 'global_mapping';
    values?: Record<string, string>;
    condition?: (context: DynamicContext) => boolean;
    trueValue?: string;
    falseValue?: string;
    rules?: Array<{
        conditions: Array<{
            questionId: number;
            option: string;
        }>;
        value: string;
    }>;
    [key: string]: any; // Permitir índices dinâmicos para os tipos de ato
}

export interface DynamicContext {
    actType: string;
    previousAnswers: string[];
    flowContext: {
        flowId?: number;
        currentQuestionId?: number;
        questionOrder?: number[];
        questionBankMapping?: Record<string, string | number>; // Mapeamento de flow_question_id para question_bank_id
        [key: string]: any;
    };
}

interface DynamicTextContextType {
    processText: (text: string) => string;
    setMappings: Dispatch<SetStateAction<Record<string, DynamicTextMapping>>>;
    updateContext: (updates: Partial<DynamicContext>) => void;
    context: DynamicContext;
    globalMappings: Record<string, Record<string, string>>;
    setGlobalMappings: Dispatch<SetStateAction<Record<string, Record<string, string>>>>;
}

// Criação do contexto com valor inicial null
const DynamicTextContext = createContext<DynamicTextContextType | null>(null);

// Função auxiliar para avaliar mapeamentos
const evaluateMapping = (
    variableName: string, 
    mapping: DynamicTextMapping, 
    context: DynamicContext, 
    allMappings: Record<string, DynamicTextMapping>,
    globalMappings?: Record<string, Record<string, string>>
): string => {
    console.log(`Avaliando mapeamento para "${variableName}"`, { mapping, context });
    
    switch (mapping.type) {
        case 'global_mapping': {
            // Verificar se temos mapeamentos globais disponíveis
            if (!globalMappings) {
                console.log('Mapeamentos globais não disponíveis');
                return '';
            }
            
            // Verificar se existe um mapeamento global para esta variável
            const variableMappings = globalMappings[variableName];
            if (!variableMappings) {
                console.log(`Nenhum mapeamento global encontrado para a variável "${variableName}"`);
                return '';
            }
            
            const actTypeName = context.actType;
            if (!actTypeName) {
                console.log('Tipo de ato não definido no contexto');
                return '';
            }
            
            // Buscar o valor no mapeamento global
            const value = variableMappings[actTypeName];
            if (value) {
                console.log(`Valor global encontrado para "${variableName}" com tipo "${actTypeName}": ${value}`);
                return value;
            }
            
            // Se não encontrou valor direto, tentar usar os valores do mapping
            if (mapping.values && mapping.values[actTypeName]) {
                const mappingValue = mapping.values[actTypeName];
                console.log(`Valor encontrado no mapping para "${variableName}" com tipo "${actTypeName}": ${mappingValue}`);
                return mappingValue;
            }
            
            console.log(`Nenhum valor global encontrado para a variável "${variableName}" com tipo "${actTypeName}"`);
            return '';
        }
        case 'act_type': {
            // Para tipo act_type, precisamos verificar no objeto values
            const actTypeName = context.actType;
            console.log(`Buscando valor para actType: "${actTypeName}"`);
            
            if (!actTypeName) {
                console.log('Tipo de ato não definido no contexto');
                return '';
            }
            
            // Buscar diretamente no objeto values
            if (mapping.values && mapping.values[actTypeName]) {
                const value = mapping.values[actTypeName];
                console.log(`Valor encontrado para tipo "${actTypeName}": ${value}`);
                return value;
            }
            
            console.log(`Nenhum valor encontrado para o tipo "${actTypeName}"`);
            return '';
        }
        case 'previous_answer': {
            if (!context.previousAnswers.length) {
                console.log('Nenhuma resposta anterior disponível');
                return '';
            }
            
            const lastAnswer = context.previousAnswers[context.previousAnswers.length - 1];
            console.log(`Última resposta: ${lastAnswer}`);
            
            // Verificar se temos informações sobre o fluxo atual
            const currentFlowId = context.flowContext?.flowId;
            console.log('ID do fluxo atual:', currentFlowId);
            
            if (currentFlowId && mapping.flow_mappings) {
                // Tentar encontrar mapeamento para o fluxo atual
                const flowKey = `flow_${currentFlowId}`;
                const flowMapping = mapping.flow_mappings[flowKey];
                
                if (flowMapping) {
                    console.log(`Encontrado mapeamento para o fluxo ${flowKey}:`, flowMapping);
                    const key = `previous_${lastAnswer}`;
                    if (key in flowMapping) {
                        console.log(`Valor encontrado para resposta "${key}" no fluxo ${flowKey}:`, flowMapping[key]);
                        return flowMapping[key];
                    }
                }
            }
            
            // Fallback para o comportamento anterior (compatibilidade)
            if (mapping.values) {
                const key = `previous_${lastAnswer}`;
                const value = mapping.values[key];
                console.log(`Valor para resposta anterior "${key}" (fallback):`, value);
                return value || '';
            }
            
            console.log('Nenhum valor mapeado encontrado para a resposta anterior');
            return '';
        }
        case 'conditional_previous_answer': {
            if (!context.previousAnswers.length || !mapping.values) {
                console.log('Nenhuma resposta anterior disponível ou valores não definidos');
                return '';
            }
            
            const lastAnswer = context.previousAnswers[context.previousAnswers.length - 1];
            console.log(`Última resposta: ${lastAnswer}`);
            
            const key = `previous_${lastAnswer}`;
            if (key in mapping.values) {
                const value = mapping.values[key];
                console.log(`Valor encontrado para resposta "${key}":`, value);
                return value;
            }
            
            console.log(`Nenhum valor mapeado encontrado para a resposta "${key}"`);
            return '';
        }
        case 'conditional': {
            if (!mapping.condition || typeof mapping.trueValue === 'undefined' || typeof mapping.falseValue === 'undefined') {
                console.log('Condição ou valores não definidos');
                return '';
            }
            
            const conditionResult = mapping.condition(context);
            console.log(`Resultado da condição:`, conditionResult);
            
            return conditionResult ? String(mapping.trueValue) : String(mapping.falseValue);
        }
        case 'conditional_multiple': {
            if (mapping.rules && mapping.rules.length > 0) {
                console.log('Rules found:', mapping.rules);
                console.log('Contexto atual para avaliação:', context);
                
                // Verificar todas as condições
                for (const rule of mapping.rules) {
                    let allConditionsMet = true;
                    
                    for (const condition of rule.conditions) {
                        console.log(`Verificando condição:`, condition);
                        
                        // Verificar diretamente as respostas anteriores, independente do mapeamento
                        let questionFound = false;
                        
                        // Listar o mapeamento disponível para depuração
                        console.log('questionBankMapping disponível:', context.flowContext.questionBankMapping);
                        
                        // Se tivermos o id do banco de questões, podemos fazer a comparação direta
                        for (const answer of context.previousAnswers) {
                            if (answer && answer.includes(':')) {
                                const [answerQuestionId, answerValue] = answer.split(':');
                                const answerQuestionIdNum = parseInt(answerQuestionId, 10);
                                
                                console.log(`Comparando resposta [${answerQuestionId}:${answerValue}] com condição [questionId=${condition.questionId}, option=${condition.option}]`);
                                
                                // Verificar se esta resposta corresponde à condição
                                if (!isNaN(answerQuestionIdNum) && answerQuestionIdNum === condition.questionId && answerValue === condition.option) {
                                    console.log(`Encontrada correspondência! QuestionId ${condition.questionId}, Option ${condition.option}`);
                                    questionFound = true;
                                    break;
                                }
                            }
                        }
                        
                        if (!questionFound) {
                            console.log(`Resposta não encontrada para questionId ${condition.questionId} e option ${condition.option}`);
                            allConditionsMet = false;
                            break;
                        }
                    }
                    
                    if (allConditionsMet) {
                        console.log(`Todas as condições atendidas para a regra, retornando valor: ${rule.value}`);
                        return rule.value;
                    }
                }
                console.log('Nenhuma regra correspondente encontrada');
            }
            return '';
        }
        default:
            console.log(`Tipo de mapeamento não suportado: ${mapping.type}`);
            return '';
    }
};

// Provider Component
export const DynamicTextProvider = ({ children }: PropsWithChildren) => {
    const [mappings, setMappings] = useState<Record<string, DynamicTextMapping>>({});
    const [globalMappings, setGlobalMappings] = useState<Record<string, Record<string, string>>>({});
    const [context, setContext] = useState<DynamicContext>({
        actType: '',
        previousAnswers: [],
        flowContext: {}
    });

    // Função para corrigir o formato dos mapeamentos
    const normalizeMapping = useCallback((data: any): Record<string, DynamicTextMapping> => {
        console.log('Normalizando mapeamento recebido:', data);
        
        if (!data || typeof data !== 'object') {
            console.log('Dados inválidos para mapeamento');
            return {};
        }
        
        const normalized: Record<string, DynamicTextMapping> = {};
        
        // Processar cada chave no objeto recebido
        Object.entries(data).forEach(([key, value]) => {
            // Verificar se já é um mapeamento com tipo
            if (value && typeof value === 'object' && 'type' in value) {
                normalized[key] = value as DynamicTextMapping;
            } 
            // Se for um objeto com valores para variáveis (tipo de ato)
            else if (value && typeof value === 'object') {
                normalized[key] = value as any;
            }
        });
        
        console.log('Mapeamento normalizado:', normalized);
        return normalized;
    }, []);

    // Log mais detalhado quando os mapeamentos são atualizados
    const setMappingsWithLogging = useCallback((newMappingsArg: SetStateAction<Record<string, DynamicTextMapping>>) => {
        console.log('Definindo novos mapeamentos (bruto):', newMappingsArg);
        
        // Normalizar os mapeamentos
        if (typeof newMappingsArg === 'function') {
            setMappings(prev => {
                const updated = newMappingsArg(prev);
                const normalized = normalizeMapping(updated);
                return normalized;
            });
        } else {
            const normalized = normalizeMapping(newMappingsArg);
            setMappings(normalized);
        }
    }, [normalizeMapping]);

    // Carregar mapeamentos globais quando o componente for montado
    useEffect(() => {
        const loadGlobalMappings = async () => {
            try {
                const response = await globalVariableService.getAllMappingsGrouped();
                const mappingsObject: Record<string, Record<string, string>> = {};
                
                // Transformar a lista em um objeto para facilitar o acesso
                response.variables.forEach((variable: GlobalVariableMappingGroup) => {
                    // Garantir que o nome da variável não inclua as chaves
                    const cleanVariableName = variable.variable_name.replace(/[{}]/g, '');
                    mappingsObject[cleanVariableName] = variable.mappings;
                });
                
                console.log('Mapeamentos globais carregados:', mappingsObject);
                setGlobalMappings(mappingsObject);
                
                // Adicionar os mapeamentos globais ao estado de mappings
                setMappingsWithLogging(prev => {
                    const newMappings = { ...prev };
                    Object.keys(mappingsObject).forEach(variableName => {
                        newMappings[variableName] = {
                            type: 'global_mapping',
                            values: mappingsObject[variableName]
                        };
                    });
                    return newMappings;
                });
            } catch (error) {
                console.error('Erro ao carregar mapeamentos globais:', error);
            }
        };
        
        loadGlobalMappings();
    }, [setMappingsWithLogging]);

    const processText = useCallback((text: string): string => {
        console.log('Processando texto:', text);
        console.log('Contexto atual:', context);
        
        if (!text) {
            console.log('Texto vazio ou nulo');
            return '';
        }
        
        // Verifica se há variáveis para substituir
        if (!text.includes('{')) {
            console.log('Texto não contém variáveis');
            return text;
        }
        
        console.log('Mapeamentos disponíveis:', mappings);
        
        return text.replace(/\{([^}]+)\}/g, (match, variable) => {
            console.log('Processando variável:', variable);
            
            // Remover chaves da variável se houver
            const cleanVariable = variable.replace(/[{}]/g, '');
            
            // Verificar se existe um mapeamento para esta variável
            const mapping = mappings[cleanVariable];
            if (!mapping) {
                console.log(`Nenhum mapeamento encontrado para: ${cleanVariable}`);
                return ''; // Ocultar a variável se não houver mapeamento
            }

            console.log(`Mapeamento encontrado para "${cleanVariable}":`, mapping);
            
            // Avaliar o mapeamento com base no tipo
            const result = evaluateMapping(cleanVariable, mapping, context, mappings, globalMappings);
            console.log(`Resultado da substituição para "${cleanVariable}":`, result);
            
            // Se o resultado for vazio, ocultar a variável
            return result || '';
        });
    }, [mappings, context, globalMappings]);

    const updateContext = useCallback((updates: Partial<DynamicContext>) => {
        console.log('Atualizando contexto:', updates);
        
        setContext(prev => {
            const newContext = { ...prev };

            // Atualizar tipo de ato se fornecido
            if (updates.actType) {
                console.log(`Mudança de tipo de ato para: "${updates.actType}"`);
                newContext.actType = updates.actType;
            }

            // Atualizar respostas anteriores se fornecidas
            if (updates.previousAnswers) {
                console.log('Atualizando respostas anteriores:', updates.previousAnswers);
                newContext.previousAnswers = [...updates.previousAnswers];
            }

            // Atualizar contexto do fluxo se fornecido
            if (updates.flowContext) {
                console.log('Atualizando contexto do fluxo:', updates.flowContext);
                
                // Garantir que temos a ordem das questões
                if (updates.flowContext.currentQuestionId && !updates.flowContext.questionOrder) {
                    // Se não temos a ordem das questões, mas temos o ID da questão atual,
                    // criar ou atualizar a ordem das questões
                    const questionOrder = [...(prev.flowContext.questionOrder || [])];
                    
                    // Adicionar o ID da questão atual se ainda não estiver na lista
                    if (!questionOrder.includes(updates.flowContext.currentQuestionId)) {
                        questionOrder.push(updates.flowContext.currentQuestionId);
                        updates.flowContext.questionOrder = questionOrder;
                        console.log('Ordem das questões atualizada:', questionOrder);
                    }
                }
                
                newContext.flowContext = { ...prev.flowContext, ...updates.flowContext };
            }

            console.log('Contexto atualizado:', newContext);
            return newContext;
        });
    }, []);

    const contextValue: DynamicTextContextType = {
        processText,
        setMappings: setMappingsWithLogging,
        updateContext,
        context,
        globalMappings,
        setGlobalMappings
    };

    return (
        <DynamicTextContext.Provider value={contextValue}>
            {children}
        </DynamicTextContext.Provider>
    );
};

// Hook personalizado
export const useDynamicText = (): DynamicTextContextType => {
    const context = useContext(DynamicTextContext);
    if (!context) {
        throw new Error('useDynamicText deve ser usado dentro de um DynamicTextProvider');
    }
    return context;
}; 